import { useEffect, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import * as Yup from "yup";
import parsePhoneNumber from "libphonenumber-js";
import { useAppContext } from "@context/app";
import { PhonePropsType } from "./types";
import { userApi } from "gate";
import { useUI } from "@context/ui-managed-context";
import useEvents from "@hooks/useEvents";

export const usePhone = (props: PhonePropsType) => {
  const t = useTranslations();
  const { country, syncData } = useAppContext();
  const [isLoading, setIsLoading] = useState(false);
  const { modalView } = useUI();
  const { verifyPhoneFormSubmitted, verifyPhoneFormFailed, verifyPhoneFormViewed } = useEvents();

  useEffect(() => {
    const suggestedPhone: string | undefined = modalView.args[0];
    verifyPhoneFormViewed(suggestedPhone ? true : false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        countryCode: Yup.string().required(t("client_errors_fieldisrequiered")),
        phoneNumber: Yup.string()
          .required(t("client_errors_fieldisrequiered"))
          .test("isValidPhoneNumber", t("client__errors_invalidphonenumber"), function (value) {
            const { countryCode } = this.parent;
            if (value && countryCode) {
              try {
                const phoneNumber = parsePhoneNumber(`${countryCode}${value}`);
                return phoneNumber ? phoneNumber.isValid() : false;
              } catch (error) {
                return false;
              }
            }
            return false;
          }),
      }),
    [t]
  );

  const initialValues = useMemo(
    () => ({
      countryCode: props.countryCode || country?.dialcode || "",
      phoneNumber: props.phoneNumber || "",
    }),
    [props.countryCode, props.phoneNumber, country]
  );

  const onSubmit = (values: { countryCode: string; phoneNumber: string }) => {
    if (isLoading) return;
    setIsLoading(true);
    const suggestedPhone: string | undefined = modalView.args[0];
    userApi
      .sendCode({ phone: `${values.countryCode}${values.phoneNumber}` })
      .fetch()
      .then((res) => {
        verifyPhoneFormSubmitted(
          `${values.countryCode}${values.phoneNumber}`,
          suggestedPhone ? true : false
        );
        props.onChangeStep("OTP", values.phoneNumber, values.countryCode, res.data.wait);
      })
      .catch((err) => {
        verifyPhoneFormFailed(
          `${values.countryCode}${values.phoneNumber}`,
          err?.meta?.msg ?? "",
          suggestedPhone ? true : false
        );
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return {
    validationSchema,
    initialValues,
    countryList: syncData?.countries || [],
    onSubmit,
    isLoading,
  };
};